import { FC, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { CheckCircle as CheckCircleIcon } from "@material-ui/icons";
import { isEqual } from "lodash";
import { usePostHog } from "posthog-js/react";

import {
  Button,
  ErrorMessage,
  Modal,
  TermsAndConditions,
  TransactionFeeNotice,
  WarningMessage,
} from "components";
import { usePrevious } from "hooks";
import {
  formatAmount,
  formatCurrencyAmount,
  formatDate,
  formatNumber,
} from "lib/format";
import { goToListingDealPage } from "lib/helpers";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { ListingWithIncludes } from "services/listings/types";
import { makeOffer } from "services/offers";
import { ListingOffer } from "services/offers/types";
import { Track } from "services/valuation/types";
import theme from "theme";
import { circleIconSize } from "theme/constants";

interface ConfirmBuyModalProps {
  open: boolean;
  onClose: () => void;
  handleRefreshListing: () => Promise<void>;
  buyNowPrice: number;
  dealId: number;
  dollarAge: number;
  listingId: number;
  lte: number;
  ltm: number;
  offers: ListingOffer[];
  termExpirationDate: string | null;
  trackList: Track[];
  valuationId: number;
  listing: ListingWithIncludes;
}

const ConfirmBuyModal: FC<ConfirmBuyModalProps> = ({
  open,
  onClose,
  handleRefreshListing,
  buyNowPrice,
  dealId,
  dollarAge,
  listingId,
  lte,
  ltm,
  offers,
  termExpirationDate,
  trackList,
  valuationId,
  listing,
}) => {
  const posthog = usePostHog();
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [statisticsChanged, setStatisticsChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { accept_final_offers } = listing;
  const titleByStep = accept_final_offers
    ? ["Offer at List Price", "Offer Confirmed"]
    : ["Commit to Purchase", "Purchase Confirmed"];
  const statistics = useMemo(
    () => ({
      lte,
      tracklistLength: trackList.length,
      buyNowPrice,
      offers,
    }),
    [lte, trackList, buyNowPrice, offers],
  );
  const previousStatistics = usePrevious(statistics);

  useEffect(() => {
    if (!!previousStatistics && !isEqual(statistics, previousStatistics)) {
      setStatisticsChanged(true);
    }
  }, [statistics, previousStatistics]);

  const handleCommitToBuy = async () => {
    try {
      setErrorMessage("");
      await makeOffer(
        listingId,
        {
          valuation_id: valuationId,
          amount: buyNowPrice,
        },
        posthog,
      );

      setStep(s => s + 1);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE);
    }
  };

  const previousDealId = usePrevious(dealId);
  useEffect(() => {
    if (!accept_final_offers) {
      if (!!dealId && !previousDealId) {
        setTimeout(() => goToListingDealPage(dealId), 3000);
      }
    }
  }, [dealId, listingId, previousDealId, listing, accept_final_offers]);

  const [hasFetchedListing, setHasFetchedListing] = useState(false);

  useEffect(() => {
    const handleRefreshListingWithLoadingState = async () => {
      try {
        setIsLoading(true);
        await handleRefreshListing();
      } catch (e) {
        // pass
      } finally {
        setIsLoading(false);
      }
    };
    if (!hasFetchedListing && open && handleRefreshListing) {
      handleRefreshListingWithLoadingState();
      setHasFetchedListing(true);
    }
    if (!open) {
      setHasFetchedListing(false);
    }
  }, [hasFetchedListing, open, handleRefreshListing]);

  return (
    <Modal title={titleByStep[step]} open={open} onClose={onClose}>
      {step === 0 && (
        <>
          <Typography component="div" gutterBottom>
            <Box fontSize={13}>
              {accept_final_offers
                ? "Please review before you commit to your offer. This will begin a 24-hour auction for this asset starting at the offer amount. If no other investors bid, you will win this asset at this price. You will have the opportunity to bid again during the final auction."
                : "Please review before you commit to purchasing this asset."}
            </Box>
          </Typography>
          <Box m={2} textAlign="center">
            <Typography component="div">
              {!isLoading && (
                <>
                  {statisticsChanged && (
                    <WarningMessage
                      title="Statistics Changed"
                      description={
                        "The earning statistics have changed for " +
                        "this catalog. Please check to confirm before " +
                        "proceeding."
                      }
                    />
                  )}
                  {errorMessage && (
                    <Box my={2}>
                      <ErrorMessage
                        title="Request Failed"
                        description={errorMessage}
                      />
                    </Box>
                  )}
                  <Box fontSize={13} textAlign="center" m={2}>
                    Confirmed Earning Statistics
                  </Box>
                  <Box m={2}>
                    <Box fontSize={15}>Earns Through</Box>
                    <Box
                      fontSize={15}
                      fontWeight={theme.typography.fontWeightMedium}
                    >
                      {formatDate(termExpirationDate)}
                    </Box>
                  </Box>
                  <Box m={2}>
                    <Box fontSize={15}>List Price*</Box>
                    <Box
                      fontSize={15}
                      fontWeight={theme.typography.fontWeightMedium}
                    >
                      {formatCurrencyAmount("USD", buyNowPrice)}
                    </Box>
                  </Box>
                  <Box m={2}>
                    <Box fontSize={15}>Last 12 Months&apos; Earnings</Box>
                    <Box
                      fontSize={15}
                      fontWeight={theme.typography.fontWeightMedium}
                    >
                      {formatAmount(ltm)}
                    </Box>
                  </Box>
                  <Box m={2}>
                    <Box fontSize={15}>Dollar Age</Box>
                    <Box
                      fontSize={15}
                      fontWeight={theme.typography.fontWeightMedium}
                    >
                      {formatNumber(dollarAge, " Years")}
                    </Box>
                  </Box>
                </>
              )}
            </Typography>
          </Box>
          <Box m={2} textAlign="center">
            {!isLoading && (
              <TransactionFeeNotice isBuyNow price={buyNowPrice} />
            )}
            <TermsAndConditions />
            <Box m={2}>
              <Button
                customColor="green"
                variant="contained"
                fullWidth
                size="large"
                onClick={handleCommitToBuy}
                disabled={isLoading}
                id="confirm-to-purchase"
              >
                {accept_final_offers
                  ? "Offer at List Price"
                  : "Confirm to Purchase"}
              </Button>
            </Box>
          </Box>
        </>
      )}
      {step === 1 && (
        <Typography component="div">
          <Box textAlign="center" mb={2}>
            <CheckCircleIcon
              style={{
                fontSize: circleIconSize.big,
                color: theme.custom.palette.green.main,
              }}
            />
          </Box>
          <Box fontSize={15} textAlign="center" m={2}>
            {accept_final_offers
              ? "The 24-hour final auction has begun."
              : "Your purchase has been confirmed. We're redirecting you to a page with next steps."}
          </Box>
        </Typography>
      )}
    </Modal>
  );
};

export default ConfirmBuyModal;
